import { classNames } from "@circle-react/helpers/twMergeWithCN";
import type { ReactNode } from "react";

import { anchorClassNames } from "@/react/helpers/cssAnchorHelpers";

/*
 * Bridge layer between the AppShell's bounded rail panel (`ShellRailPanel`)
 * and the shared `Rail` UIKit component (`@circle-react-uikit/Rail`).
 *
 * `Rail` was designed for the legacy `StandardLayoutV2` chrome where the
 * railbar is anchored to the viewport (`position: fixed`, `top: …`, inline
 * width style). Inside the AppShell the rail lives in a flexbox child with
 * known bounded dimensions, so all that absolute positioning fights the
 * container. These overrides drop `Rail` back to plain block flow + 100%
 * fill inside the panel.
 *
 * Long-term, the right fix is an `inline` prop on `Rail` that elides its
 * fixed/absolute positioning when consumers tell it to. That's its own PR
 * because `Rail` has many consumers (chat, threads, courses, live streams,
 * messaging modals, AI summary…); when that lands, delete this file and
 * replace its usage in `ShellNativeContent` with `<PageRailbarContainer />`
 * directly.
 */

const RAIL_OVERRIDES = classNames(
  "min-h-0 flex-1 overflow-auto",
  "[&_#rail-bar-header]:!h-full",
  "[&_#rail-bar-header>div]:!w-full",
  "[&_#rail-bar-header>div]:!h-full",
  "[&_#rail-content]:!relative",
  "[&_#rail-content]:!inset-auto",
  "[&_#rail-content]:!w-full",
  "[&_#rail-content]:!h-full",
  "[&_#rail-content]:!z-auto",
  // `ShellRailPanel`'s <aside> already draws the panel's left border, so
  // drop `Rail`'s own legacy `border-l` to avoid a doubled-up divider.
  "[&_#rail-content]:!border-l-0",
  anchorClassNames("standard-layout-v2__railbar"),
);

interface ShellRailContentProps {
  children: ReactNode;
}

export const ShellRailContent = ({ children }: ShellRailContentProps) => (
  <div className={RAIL_OVERRIDES}>{children}</div>
);
